import { HD_URL } from '../constants';

// Helper to check if a breadcrumb item is valid
const isValidBreadcrumbItem = (item) => {
  if (!item) {
    return false;
  }
  const hasName = Boolean(item.name || item.label);
  const hasId = Boolean(item.id || item.url);
  return hasName && hasId;
};

/**
 * Generates a Schema.org BreadcrumbList object for structured data.
 *
 * @param {Array} items - An array of breadcrumb items. Each item should have a `name` or `label`, and an `id` or `url`.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.addHome=true] - Whether to prepend a "Home" breadcrumb.
 * @param {string} [options.urlPrefix=HD_URL] - Prefix to use for relative URLs.
 * @param {boolean} [options.browse=false] - Whether to use `Thing` type for items (for browse/search pages).
 * @returns {Object} A JSON-LD BreadcrumbList object suitable for use in structured data.
 */
export function generateBreadcrumbList(items, options = {}) {
  const { addHome = true, urlPrefix = HD_URL, browse = false } = options;

  // Filter out items missing required fields
  let validItems = items.filter(isValidBreadcrumbItem);

  // Add "Home" at the beginning if needed
  const firstItemLabel = validItems[0]?.name || validItems[0]?.label;
  if (addHome && firstItemLabel !== 'Home') {
    validItems = [{ name: 'Home', id: HD_URL }, ...validItems];
  }

  const list = validItems.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      ...(browse && { '@type': 'Thing' }),
      name: item.name || item.label,
      '@id': item.id || urlPrefix + item.url
    }
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list
  };
}
